/**
 * Step-up na interface (H13, consumindo a B17).
 *
 * ── O Aceite que este arquivo fecha ───────────────────────────────────────
 *
 * *"Step-up com contexto: dizer QUAL ação exige reautenticação e por quê.
 * Pedir senha sem explicar parece falha."*
 *
 * E o texto não é escrito aqui. Ele vem no 403, na extensão `step_up` que a
 * B17 acrescentou ao problema — `descricao`, `porque`, `motivo` e se exige
 * segundo fator. Manter uma cópia do catálogo no cliente seria criar duas
 * versões que divergem no dia em que alguém acrescentar uma ação sensível.
 *
 * ── A ordem: explicar, reautenticar, justificar, refazer ──────────────────
 *
 * A pessoa clica em algo comum, toma o 403, e o diálogo abre já dizendo o que
 * vai acontecer. Ela reautentica numa janela do provedor, escreve o motivo, e a
 * ação original é refeita — sem que ela tenha de encontrar o botão de novo.
 *
 * Reautenticar ANTES de escrever a justificativa é deliberado: o servidor conta
 * cinco minutos desde a autenticação, e escrever um texto cuidadoso leva mais
 * tempo do que abrir um popup. A ordem inversa faria a concessão ser recusada
 * justamente para quem escreveu a melhor justificativa.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useApi } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useSessao } from '@/shared/sessao/contexto';
import { revendaAtual } from '@/shared/sessao/revenda';
import { reautenticarParaStepUp } from '@/shared/sessao/fluxo-oidc';
import './step-up.css';
import { Botao, CampoArea, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';

/**
 * O teto da justificativa: a tela avisa antes, e o campo não passa dele. O
 * servidor recusa acima dele só nas ações da gestão de organizações (#1160);
 * nas outras, o teto é só desta tela.
 */
const MAIOR_JUSTIFICATIVA = 500;

/** O que o 403 traz quando a permissão volta com reautenticação. */
export interface ExigenciaDeStepUp {
  readonly acao: string;
  readonly descricao: string;
  readonly porque: string;
  readonly exige_mfa: boolean;
  /**
   * A ação pede a justificativa escrita?
   *
   * `?? true` na leitura: uma API mais velha que esta tela não manda o
   * campo, e o padrão seguro é PEDIR. Assumir `false` esconderia um campo
   * que o servidor ainda exige, e o botão ficaria recusando sem dizer por quê.
   */
  readonly exige_justificativa: boolean;
  readonly motivo: string;
}

/**
 * Lê a exigência de um erro, ou devolve `undefined`.
 *
 * O cliente da API lança um erro que carrega o corpo do problema. Só interessa
 * o 403 QUE TEM SAÍDA — um 403 comum continua sendo "sem permissão", e abrir um
 * diálogo de reautenticação nele prometeria um caminho que não existe.
 */
export function exigenciaDe(erro: unknown): ExigenciaDeStepUp | undefined {
  const problema = (
    erro as {
      problema?: {
        code?: string;
        step_up?: Omit<ExigenciaDeStepUp, 'exige_justificativa'> & {
          exige_justificativa?: boolean;
        };
      };
    }
  )?.problema;
  if (problema?.code !== 'SEM_PERMISSAO') return undefined;
  if (!problema.step_up) return undefined;

  return {
    ...problema.step_up,
    /*
     * `?? true` — o padrão é PEDIR.
     *
     * Uma API mais velha que esta tela não manda o campo. Assumir `false` ali
     * esconderia um campo que o servidor ainda exige, e o botão passaria a
     * recusar sem dizer por quê. Assumir `true` no máximo mostra um campo a
     * mais, que é o comportamento de antes.
     */
    exige_justificativa: problema.step_up.exige_justificativa ?? true,
  };
}

type Fase =
  | 'explicando'
  | 'reautenticando'
  | 'justificando'
  | 'concedendo'
  | 'falhou'
  /** Concedido, mas a ação refeita falhou e quem chamou não pediu o erro. */
  | 'acao-falhou';

export interface UsoDeStepUp {
  /** A exigência aberta, ou `null` quando não há diálogo na tela. */
  readonly exigencia: ExigenciaDeStepUp | null;
  /**
   * Envolve uma ação sensível: roda, e se o servidor pedir step-up, abre o
   * diálogo e REFAZ a ação depois da concessão.
   *
   * A ação refeita roda fora de quem chamou (a mutação dele já terminou).
   * `aoFalhar` recebe o erro dela, para a tela mostrá-lo onde mostra os
   * outros; sem ele, o diálogo reabre com a falha. Antes, o erro escapava ao
   * console e a tela não dizia nada (#1093).
   *
   * `sugestao` é a justificativa que a pessoa já escreveu para a própria ação
   * (excluir uma organização pede o motivo no corpo do pedido): o campo do
   * diálogo abre com ela, e a pessoa não escreve a mesma frase duas vezes.
   * Refeita depois da concessão, a ação recebe a justificativa concedida: se a
   * pessoa a corrigiu no diálogo, é a corrigida que vai no pedido (#1160).
   */
  readonly comStepUp: (
    acao: (justificativaConcedida?: string) => Promise<void>,
    alvo?: string,
    aoFalhar?: (erro: unknown) => void,
    sugestao?: string,
  ) => Promise<void>;
  /** A justificativa com que o campo do diálogo abre; vazia quando não há. */
  readonly sugestao: string;
  readonly fechar: () => void;
  readonly fase: Fase;
  readonly falha: string | null;
  readonly reautenticar: () => Promise<void>;
  readonly conceder: (justificativa: string) => Promise<void>;
}

export function useStepUp(): UsoDeStepUp {
  const api = useApi();
  const { estado } = useSessao();
  const [exigencia, definirExigencia] = useState<ExigenciaDeStepUp | null>(null);
  const [fase, definirFase] = useState<Fase>('explicando');
  const [falha, definirFalha] = useState<string | null>(null);
  const [sugestao, definirSugestao] = useState('');

  // A ação original e o alvo, guardados para refazer depois da concessão.
  const pendente = useRef<{
    acao: (justificativaConcedida?: string) => Promise<void>;
    alvo?: string;
    aoFalhar?: (erro: unknown) => void;
    sugestao?: string;
  } | null>(null);

  /**
   * A última justificativa concedida por este diálogo. O servidor recusa a
   * igual à última concessão da pessoa: o campo não abre com ela (#1160).
   */
  const ultimaConcedida = useRef<string | null>(null);

  const fechar = useCallback(() => {
    pendente.current = null;
    definirExigencia(null);
    definirFalha(null);
    definirFase('explicando');
    definirSugestao('');
  }, []);

  /**
   * Roda a ação e, se o servidor pedir o segundo fator, guarda-a para refazer.
   * Na volta da concessão, `concedida` é a justificativa usada, e vai para a
   * ação refeita.
   */
  const tentar = useCallback(
    async (
      acao: (justificativaConcedida?: string) => Promise<void>,
      alvo?: string,
      aoFalhar?: (erro: unknown) => void,
      sugestaoDaAcao?: string,
      concedida?: string,
    ) => {
      try {
        await acao(concedida);
      } catch (erro: unknown) {
        const pedida = exigenciaDe(erro);
        // Sem extensão, o erro é de outra natureza e sobe: transformar tudo em
        // step-up esconderia falha de rede atrás de um pedido de senha.
        if (!pedida) throw erro;

        // O campo abre vazio quando o servidor recusaria a sugestão como
        // repetida (#1160): pedido de novo logo depois de uma concessão, ou a
        // sugestão da ação igual à última concedida (a ação a mostra depois de
        // concedida, e a pessoa repete).
        const repetiria =
          sugestaoDaAcao !== undefined &&
          ultimaConcedida.current !== null &&
          comoOServidorCompara(sugestaoDaAcao) === comoOServidorCompara(ultimaConcedida.current);
        const sugestaoDaVez = concedida === undefined && !repetiria ? sugestaoDaAcao : undefined;
        pendente.current = {
          acao,
          ...(alvo === undefined ? {} : { alvo }),
          ...(aoFalhar === undefined ? {} : { aoFalhar }),
          ...(sugestaoDaAcao === undefined ? {} : { sugestao: sugestaoDaAcao }),
        };
        definirSugestao(sugestaoDaVez ?? '');
        definirExigencia(pedida);
        definirFase('explicando');
        definirFalha(null);
      }
    },
    [],
  );

  const comStepUp = useCallback(
    (
      acao: (justificativaConcedida?: string) => Promise<void>,
      alvo?: string,
      aoFalhar?: (erro: unknown) => void,
      sugestaoDaAcao?: string,
    ) => tentar(acao, alvo, aoFalhar, sugestaoDaAcao),
    [tentar],
  );

  const reautenticar = useCallback(async () => {
    const revenda = estado.nome === 'autenticado' ? estado.me.tenant.slug : revendaAtual();
    if (!revenda) {
      definirFalha('Não sei de qual organização é esta sessão. Entre de novo.');
      definirFase('falhou');
      return;
    }

    definirFase('reautenticando');
    definirFalha(null);

    const confirmou = await reautenticarParaStepUp(revenda);
    if (!confirmou) {
      // Popup bloqueado ou janela fechada. Não é erro do servidor, e a saída é
      // tentar de novo — não recarregar a página, que perderia o rascunho.
      definirFalha(
        'A confirmação não foi concluída. Se o navegador bloqueou a janela, libere-a e ' +
          'tente de novo.',
      );
      definirFase('falhou');
      return;
    }

    definirFase('justificando');
  }, [estado]);

  const conceder = useCallback(
    async (justificativa: string) => {
      if (!api || !exigencia) return;

      definirFase('concedendo');
      definirFalha(null);

      try {
        await api.pedirStepUp({
          corpo: {
            acao: exigencia.acao,
            ...(pendente.current?.alvo === undefined ? {} : { alvo: pendente.current.alvo }),
            justificativa,
          },
        });
      } catch (erro: unknown) {
        definirFalha(mensagemDe(erro));
        definirFase('justificando');
        return;
      }
      ultimaConcedida.current = justificativa;

      // Concedido: refaz a ação original, com a justificativa concedida. Se ela
      // pedir step-up de novo (a concessão venceu entre uma coisa e outra), o
      // diálogo reabre, que é o certo.
      const guardada = pendente.current;
      const exigida = exigencia;
      fechar();
      if (!guardada) return;
      try {
        await tentar(
          guardada.acao,
          guardada.alvo,
          guardada.aoFalhar,
          guardada.sugestao,
          justificativa,
        );
      } catch (erro: unknown) {
        if (guardada.aoFalhar) {
          guardada.aoFalhar(erro);
          return;
        }
        // A ação pediu step-up de novo e o diálogo já reabriu com a exigência
        // nova: não a troque pela antiga, já usada.
        if (pendente.current) return;
        // Ninguém pediu o erro: o diálogo volta para dizê-lo, em vez de a
        // falha sumir no console com a tela parada.
        definirExigencia(exigida);
        definirFalha(recadoDeErro(erro, { generica: 'A ação não foi concluída. Tente de novo.' }));
        definirFase('acao-falhou');
      }
    },
    [api, exigencia, fechar, tentar],
  );

  return { exigencia, comStepUp, sugestao, fechar, fase, falha, reautenticar, conceder };
}

/** Como o servidor compara com a última concedida: sem as pontas, espaços juntos, sem caixa. */
function comoOServidorCompara(texto: string): string {
  return texto.trim().replace(/\s+/g, ' ').toLowerCase();
}

function mensagemDe(erro: unknown): string {
  const problema = (erro as { problema?: { detail?: string; errors?: { problema: string }[] } })
    ?.problema;
  return (
    problema?.errors?.[0]?.problema ??
    problema?.detail ??
    'Não foi possível registrar a autorização. Tente de novo.'
  );
}

/**
 * O que ainda falta na justificativa, em palavras — ou `null` quando está boa.
 *
 * ── Por que a regra vive nos dois lados ───────────────────────────────────
 *
 * O servidor continua sendo a barreira: esta função não decide nada, só avisa
 * antes. A diferença é entre saber enquanto digita e descobrir depois de
 * clicar — e em 02/09/2026 alguém tentou SEIS vezes porque a recusa dizia
 * apenas "sem conteúdo", sem contar qual das regras tinha sido quebrada.
 *
 * ── Os três números são os do servidor ────────────────────────────────────
 *
 * 10 caracteres, 3 palavras de mais de uma letra, 8 caracteres distintos. O
 * último é o que barra texto de preenchimento: `asdasd asdasd asdasd` tem
 * palavras e tamanho de sobra, e três letras diferentes.
 *
 * Divergir daqui produziria o pior dos mundos — a tela aceita, o servidor
 * recusa, e a pessoa conclui que o sistema está quebrado.
 */
export function oQueFalta(texto: string): string | null {
  const limpo = texto.trim().replace(/\s+/g, ' ');
  if (limpo === '') return 'Escreva o motivo em uma frase.';
  if (limpo.length > MAIOR_JUSTIFICATIVA) {
    return `Escreva em até ${MAIOR_JUSTIFICATIVA} caracteres.`;
  }

  if (limpo.length < 10) {
    const faltam = 10 - limpo.length;
    return `Faltam ${faltam} ${faltam === 1 ? 'caractere' : 'caracteres'}.`;
  }

  const palavras = limpo.split(' ').filter((p) => p.length > 1);
  if (palavras.length < 3) return 'Escreva uma frase — pelo menos três palavras.';

  const distintos = new Set(limpo.toLowerCase().replace(/\s/g, '')).size;
  if (distintos < 8) {
    return 'Use letras variadas: isto parece texto de preenchimento, e não passa numa auditoria.';
  }

  return null;
}

/**
 * O diálogo.
 *
 * `alertdialog` e não `dialog`: exige uma decisão antes de continuar, e é o
 * papel que faz o leitor de tela anunciar o conteúdo inteiro em vez de só o
 * título.
 */
export function DialogoDeStepUp({ uso }: { uso: UsoDeStepUp }): ReactNode {
  const [justificativa, definirJustificativa] = useState('');
  const falta = oQueFalta(justificativa);

  // Cada pedido novo abre o campo com a justificativa que a ação já trouxe, e
  // não com o texto que sobrou do pedido anterior.
  useEffect(() => {
    definirJustificativa(uso.sugestao);
  }, [uso.exigencia, uso.sugestao]);

  if (!uso.exigencia) return null;
  const { exigencia, fase, falha } = uso;

  const acoes =
    fase === 'explicando' ? (
      <>
        <Botao onClick={uso.fechar}>Cancelar</Botao>
        <Botao variante="primario" onClick={() => void uso.reautenticar()}>
          Confirmar identidade
        </Botao>
      </>
    ) : fase === 'justificando' || fase === 'concedendo' ? (
      <>
        <Botao onClick={uso.fechar}>Cancelar</Botao>
        <Botao
          type="submit"
          form="stepup-formulario"
          variante="primario"
          desabilitado={fase === 'concedendo' || (exigencia.exige_justificativa && falta !== null)}
          motivo={
            fase === 'concedendo' ? 'Autorizando…' : 'Escreva a justificativa para continuar.'
          }
        >
          {fase === 'concedendo' ? 'Autorizando…' : 'Autorizar e continuar'}
        </Botao>
      </>
    ) : fase === 'acao-falhou' ? (
      <Botao onClick={uso.fechar}>Fechar</Botao>
    ) : fase === 'falhou' ? (
      <>
        <Botao onClick={uso.fechar}>Fechar</Botao>
        <Botao variante="primario" onClick={() => void uso.reautenticar()}>
          Tentar de novo
        </Botao>
      </>
    ) : null;

  return (
    <Dialogo
      // Depois da concessão, o que falhou foi a ação, não a confirmação: o
      // título não pode sugerir que a identidade foi recusada.
      titulo={fase === 'acao-falhou' ? 'A ação não foi concluída' : 'Confirme sua identidade'}
      papel="alertdialog"
      descricaoId="stepup-porque"
      aoFechar={uso.fechar}
      largura="estreita"
      botoes={acoes}
    >
      {/* QUAL ação, e por quê. Os dois vêm do servidor. */}
      <p className="dialogo__acao">{exigencia.descricao}</p>
      <p id="stepup-porque" className="dialogo__texto">
        {fase === 'acao-falhou'
          ? 'Sua identidade foi confirmada, mas a ação falhou depois.'
          : exigencia.porque}
      </p>

      {fase === 'explicando' && (
        <>
          <p className="dialogo__texto">{exigencia.motivo}</p>
          {exigencia.exige_mfa && (
            <p className="stepup__mfa">Esta ação exige o aplicativo autenticador, além da senha.</p>
          )}
        </>
      )}

      {fase === 'reautenticando' && (
        <p className="stepup__espera" aria-live="polite">
          Abri uma janela do provedor. Confirme por lá e volte para cá.
        </p>
      )}

      {(fase === 'justificando' || fase === 'concedendo') && (
        <form
          id="stepup-formulario"
          className="stepup__formulario"
          onSubmit={(e) => {
            e.preventDefault();
            void uso.conceder(justificativa);
          }}
        >
          {exigencia.exige_justificativa ? (
            <>
              <label className="stepup__rotulo" htmlFor="stepup-justificativa">
                Por que você precisa fazer isso?
              </label>
              <CampoArea
                id="stepup-justificativa"
                rows={3}
                value={justificativa}
                onChange={(e) => definirJustificativa(e.target.value)}
                placeholder="ex.: trocando a credencial do número da unidade centro, chamado #412"
                aria-describedby="stepup-apoio stepup-falta"
                required
                minLength={10}
                maxLength={MAIOR_JUSTIFICATIVA}
              />
              <p id="stepup-apoio" className="dialogo__apoio">
                Este texto fica registrado com o seu nome e é o que a organização apresenta numa
                auditoria.
              </p>
              {/*
                O que FALTA, enquanto digita — e não depois de clicar.
                A recusa do servidor chegava com "sem conteúdo", que não ensina
                nada: alguém tentou seis vezes em 02/09 escrevendo `asdasd`,
                porque a regra que ele quebrava (letras variadas) não estava
                escrita em lugar nenhum. `aria-live` para quem ouve a tela
                receber o mesmo aviso que quem a vê.
              */}
              {falta !== null && (
                <p id="stepup-falta" className="stepup__falta" aria-live="polite">
                  {falta}
                </p>
              )}
            </>
          ) : (
            /*
              Sem justificativa: a tela DIZ que não vai pedir.
              Um formulário que some sem explicação faz a pessoa procurar o
              campo que ela viu da última vez.
            */
            <p className="dialogo__apoio">
              Esta ação não pede justificativa escrita. Fica registrado quem confirmou e quando.
            </p>
          )}

          {falha && <Faixa tom="bloqueado">{falha}</Faixa>}
        </form>
      )}

      {(fase === 'falhou' || fase === 'acao-falhou') && <Faixa tom="bloqueado">{falha}</Faixa>}
    </Dialogo>
  );
}
